import React, {Context, FC, PropsWithChildren, useCallback, useEffect, useState} from "react";
import {BehaviorSubject, map} from "rxjs";
import {Outlet, useParams} from "react-router-dom";

type provide = <T>(provider: Context<T>, value: T) => provide

export const Providing: FC<PropsWithChildren<{ _: (_: provide) => void }>> = ({_, children}) => {
  let element = <>{children}</>
  _(function _({Provider}, value) {
    element = <Provider value={value}>{element}</Provider>
    return _
  })
  return element
}

export const ProvideParams: FC<{
  params: Record<string, Context<string>>,
  children?: PropsWithChildren['children']
}> = ({params, children = <Outlet/>}) => {
  const p = useParams()
  return <Providing _={_ => Object.entries(params).forEach(([key, context]) => _(context, p[key] || ''))}>
    {children}
  </Providing>
}


export function useBehaviorSubject<T>(subject: BehaviorSubject<T>): [T, (_: T) => void] {
  const [state, setState] = useState(() => subject.getValue())
  const set = useCallback<(_: T) => void>(v => subject.next(v), [subject])
  useEffect(() => {
    const subscription = subject.subscribe(setState)
    return () => subscription.unsubscribe()
  }, [subject, setState])
  return [state, set]
}

export function useBehaviorSubjectWithLens<A, B>(subject: BehaviorSubject<A>, get: (_: A) => B, set: (a: A, b: B) => A): [B, (_: B) => void] {
  const [state, setState] = useState(() => get(subject.getValue()))
  const setter = useCallback<(_: B) => void>(v => subject.next(set(subject.value, v)), [set, subject])
  useEffect(() => {
    const subscription = subject.pipe(map(get)).subscribe(_ => setState(_))
    return () => subscription.unsubscribe()
  }, [subject, setState, get])
  return [state, setter]
}

export function useChild<T extends object, K extends keyof T>(subject: BehaviorSubject<T>, key: K): [T[K], (_: T[K]) => void] {
  const get = useCallback<(_: T) => T[K]>((t) => t[key], [key])
  const set = useCallback<(parent: T, child: T[K]) => T>((parent, child) => ({...parent, [key]: child}), [key])
  return useBehaviorSubjectWithLens(subject, get, set)
}

export function runCatching<T>(f: () => T): { state: 'success' | 'failure', value?: T, error?: any } {
  try {
    return {state: 'success', value: f()}
  } catch (error) {
    return {state: 'failure', error}
  }
}

export function tuple<T extends any[]>(...t: T): T {
  return t
}

export const Render: FC<{ children: () => ReturnType<FC> }> = ({children}) => children()

export function classNames(...names: (string | null | undefined | false)[]): string {
  return names?.filter(Boolean).join(' ')
}
